import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import PropTypes from 'prop-types'
import React from 'react'

import CloudFileUploader from 'app/components/CloudFileUploader'
import { AutoComplete } from 'app/components/generic/FormFields'
import ModalForm from 'app/components/ModalForm'
import { uploadPresets } from 'app/config'
import constants from 'app/constants'
import { eventAlert, eventEmitter } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  // Two columns only where a form passes `preview`; everything else keeps the
  // single column it had. `minWidth: 0` on the form column is what stops a
  // long label or a wide input from pushing the preview off the dialog.
  withPreview: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: { flexDirection: 'column' }
  },
  formCol: { flex: '1 1 auto', minWidth: 0 },
  previewCol: { flex: '0 0 auto', width: 300, maxWidth: '100%' },
  field: { marginBottom: theme.spacing(2), minWidth: 220 },
  imageRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  preview: {
    width: 44,
    height: 44,
    objectFit: 'contain',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`
  },
  previewEmpty: {
    width: 44,
    height: 44,
    borderRadius: 6,
    border: `1px dashed ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    color: theme.palette.text.disabled
  }
}))

/**
 * Schema-driven create/edit modal for the At Home list pages.
 *
 * fields: [{ name, label, type, options?, multiline? }]
 *   type: 'text' | 'number' | 'date' | 'time' | 'select' | 'multiselect'
 *         | 'checkbox' | 'textarea' | 'image'
 * Holds its own draft state seeded from `initialValues`; calls onSubmit(values).
 */
const AtHomeFormModal = ({
  open,
  title,
  fields,
  initialValues,
  onSubmit,
  // Dialog width; 'md' for forms with a table in them (module session plan).
  maxWidth = 'sm',
  // Optional: (values) => node, drawn beside the fields and re-rendered as
  // they are typed. Categories use it to show the card a parent will see
  // (owner, 2026-10-19). Left undefined, the form is exactly as it was —
  // single column, no wrapper — so no other form moves.
  preview,
  onClose
}) => {
  const classes = useStyles()
  const [values, setValues] = React.useState(initialValues || {})
  // Own saving flag, passed to ModalForm as `loading`. ModalForm's built-in
  // lock disables Save on click and re-enables it only on an error or close
  // event — so after a SUCCESSFUL save the reused dialog opened with Save
  // dead (owner, 2026-10-08: "edit once fine, edit again Save disabled").
  const [saving, setSaving] = React.useState(false)
  /**
   * One debounce timer per searchable field, for `searchselect`'s `onSearch`.
   * A ref rather than state: changing it must not re-render, and the pending
   * timer has to survive the re-render that typing causes or every keystroke
   * would start a fresh 300ms and none of them would ever fire.
   */
  const searchTimers = React.useRef({})
  React.useEffect(
    () => () => {
      // Closing the dialog must not leave a search in flight that lands on a
      // form which no longer exists.
      Object.values(searchTimers.current).forEach(clearTimeout)
    },
    []
  )

  // Seed the draft when the modal OPENS — and only then.
  //
  // This used to depend on `initialValues`, which every call site rebuilds on
  // each render: `emptyValues` is an object literal in the page body, and
  // `rowToForm(modal.row)` allocates inline. So ANY re-render of the page while
  // the modal was open reset the draft mid-edit and silently threw away what
  // had been entered.
  //
  // On Waitlist that lost the field you had just filled in: choosing a parent
  // fires `onSelect` -> `setChildren(...)` -> the page re-renders -> a new
  // `initialValues` identity -> reset -> the parent was wiped while the child
  // options it had just loaded stayed on screen. The POST then went out with
  // `parent: null`, which the API rejects with "Pick the parent first — a child
  // is chosen through them". Same trap for every field typed before the pick.
  //
  // The reset cannot simply be dropped: all four call sites keep this component
  // mounted and only toggle `open`, so without it the second row you opened
  // would still be showing the first one's draft.
  const wasOpen = React.useRef(false)
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      setValues(initialValues || {})
      setSaving(false)
    }
    wasOpen.current = open
  }, [open, initialValues])

  const setField = (name, value) =>
    setValues((prev) => ({ ...prev, [name]: value }))

  // Fields may carry `showIf: (values) => bool` to appear only once an earlier
  // choice is made (Listings: everything waits for the category).
  const visible = (fields || []).filter((f) => !f.showIf || f.showIf(values))

  const renderField = (field) => {
    const value = values[field.name] ?? ''
    switch (field.type) {
      // A page-supplied editor: field.render({ values, setField }).
      case 'custom':
        return (
          <div key={field.name} className={classes.field}>
            {field.render({ values, setField })}
          </div>
        )
      case 'note':
        return (
          <Typography
            key={field.name}
            variant="body2"
            color="textSecondary"
            className={classes.field}
          >
            {typeof field.label === 'function'
              ? field.label(values)
              : field.label}
          </Typography>
        )
      // A select you can type into: for long lists (every teacher in the
      // system). options: [{ value, label }]; the form value is the `value`.
      case 'searchselect': {
        const opts = field.options || []
        const current = opts.find((o) => o.value === value) || null
        return (
          <Autocomplete
            key={field.name}
            className={classes.field}
            options={opts}
            value={current}
            onChange={(e, opt) => setField(field.name, opt ? opt.value : '')}
            /**
             * SERVER-SIDE SEARCH, when the field asks for it.
             *
             * Without `onSearch` this is a local filter over whatever options
             * were handed in, which is fine for eight buildings and wrong for
             * 1,250 teachers: the list endpoint returns the first 200 by first
             * name, so everyone from "Bi" onwards simply did not exist as far
             * as the form was concerned, and typing their name fired no
             * request at all -- it filtered the same 200 and said "No match".
             *
             * Only `reason === 'input'` triggers it. MUI also fires this on
             * 'reset' (when a value is chosen or the box is cleared), and
             * searching for the label we just selected would replace the
             * option list underneath the selection.
             */
            onInputChange={(e, text, reason) => {
              if (!field.onSearch || reason !== 'input') return
              clearTimeout(searchTimers.current[field.name])
              searchTimers.current[field.name] = setTimeout(
                () => field.onSearch(text),
                300
              )
            }}
            // The server already searched; filtering again locally would hide
            // rows it deliberately returned (a phone-number match never looks
            // like the name being typed).
            filterOptions={field.onSearch ? (o) => o : undefined}
            getOptionLabel={(o) => o?.label || ''}
            getOptionSelected={(o, v) => o.value === v.value}
            size="small"
            noOptionsText={field.onSearch ? 'Type a name or phone number' : 'No match'}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                size="small"
                placeholder="Type to search"
              />
            )}
          />
        )
      }
      case 'multiselect':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            size="small"
            className={classes.field}
            fullWidth
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              multiple
              label={field.label}
              value={
                Array.isArray(values[field.name]) ? values[field.name] : []
              }
              onChange={(e) => setField(field.name, e.target.value)}
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {selected.map((v) => {
                    const opt = (field.options || []).find(
                      (o) => (typeof o === 'object' ? o.value : o) === v
                    )
                    const lbl = opt && typeof opt === 'object' ? opt.label : v
                    // × on each chip: picking an item again in the menu also
                    // removes it, but nobody discovers that.
                    return (
                      <Chip
                        key={v}
                        label={lbl}
                        size="small"
                        onMouseDown={(e) => e.stopPropagation()}
                        onDelete={() =>
                          setField(
                            field.name,
                            selected.filter((x) => x !== v)
                          )
                        }
                      />
                    )
                  })}
                </div>
              )}
            >
              {(field.options || []).map((opt) => {
                const val = typeof opt === 'object' ? opt.value : opt
                const lbl = typeof opt === 'object' ? opt.label : opt
                return (
                  <MenuItem key={val} value={val}>
                    {lbl}
                  </MenuItem>
                )
              })}
            </Select>
            {field.help ? <FormHelperText>{field.help}</FormHelperText> : null}
          </FormControl>
        )
      case 'select':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            size="small"
            className={classes.field}
            fullWidth
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              label={field.label}
              value={value}
              onChange={(e) => {
                setField(field.name, e.target.value)
                // Lets a dependent field react — e.g. picking a child reloads
                // the batches they may trial in.
                if (field.onSelect) field.onSelect(e.target.value)
                ;(field.clears || []).forEach((name) => setField(name, ''))
              }}
            >
              {(field.options || []).map((opt) => {
                const val = typeof opt === 'object' ? opt.value : opt
                const lbl = typeof opt === 'object' ? opt.label : opt
                return (
                  <MenuItem key={val} value={val}>
                    {lbl}
                  </MenuItem>
                )
              })}
            </Select>
            {field.help ? <FormHelperText>{field.help}</FormHelperText> : null}
          </FormControl>
        )
      // A user picker: searchable by name or phone number, server-side.
      // `field.fetchValues(text)` -> [{ uuid, name }]. `field.onSelect(opt)`
      // lets a dependent field (e.g. child) react to the choice.
      //
      // AutoComplete is uncontrolled, so editing a row needs `field.initialValue`
      // ({ uuid, name }) to render what is already selected — setting the form
      // value alone would leave the box looking empty.
      case 'autocomplete':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            className={classes.field}
            fullWidth
          >
            <AutoComplete
              // Opt-ins: 300ms throttle instead of the shared
              // 5000ms default (five seconds of an empty box reads as broken),
              // and fetch-on-open so clicking shows options instead of an empty
              // menu. Both default to the OLD behaviour everywhere else in Alfred.
              throttleMs={300}
              fetchOnOpen
              async
              name={field.name}
              label={field.label}
              multiple={false}
              initialValue={field.initialValue}
              // Without these the box is permanently empty: `handleSearch`
              // gates on `value.length >= minCharactersToSearch`, and an
              // undefined threshold makes that comparison always false, so no
              // fetch ever fires — not on mount, and not on typing either.
              minCharactersToSearch={0}
              searchWithEmptyValue
              fetchOnMount={field.fetchOnMount}
              fetchValues={field.fetchValues}
              onChange={(e, opt) => {
                setField(field.name, opt?.uuid || '')
                if (field.onSelect) field.onSelect(opt || null)
                ;(field.clears || []).forEach((name) => setField(name, ''))
              }}
              size="small"
            />
          </FormControl>
        )
      // Upload straight to Cloudinary from the browser and keep only the URL.
      // The whole widget already exists (CloudFileUploader + an unsigned preset),
      // so this case is a wiring, not a new uploader. `fileUrlOnly` is what makes
      // the form value a plain string the API can store in a URLField.
      //
      // The preview is the point: without it the only feedback after choosing a
      // file is a "See Uploaded File" link, and nobody checks a link to find out
      // they picked the wrong PNG.
      case 'image':
        return (
          <div key={field.name} className={classes.field}>
            <InputLabel shrink>{field.label}</InputLabel>
            <div className={classes.imageRow}>
              {value ? (
                <img
                  src={value}
                  alt={field.label}
                  className={classes.preview}
                />
              ) : (
                <div className={classes.previewEmpty}>none</div>
              )}
              <CloudFileUploader
                name={field.name}
                label={field.uploadLabel || 'icon'}
                uploadedFileUrl={value || ''}
                uploadPreset={
                  uploadPresets[field.preset || constants.SUBJECT_CATEGORY]
                }
                fileUrlOnly
                onUpload={({ data }) =>
                  setField(field.name, data?.secure_url || '')
                }
                setFieldValue={() => null}
              />
              {value ? (
                <Button size="small" onClick={() => setField(field.name, '')}>
                  remove
                </Button>
              ) : null}
            </div>
            {field.help ? <FormHelperText>{field.help}</FormHelperText> : null}
          </div>
        )
      case 'checkbox':
        return (
          <FormControlLabel
            key={field.name}
            className={classes.field}
            control={
              <Checkbox
                checked={Boolean(values[field.name])}
                onChange={(e) => setField(field.name, e.target.checked)}
              />
            }
            label={field.label}
          />
        )
      case 'date':
      case 'datetime':
      case 'time':
        return (
          <TextField
            key={field.name}
            className={classes.field}
            type={
              // eslint-disable-next-line no-nested-ternary
              field.type === 'datetime'
                ? 'datetime-local'
                : field.type === 'time'
                ? 'time'
                : 'date'
            }
            label={field.label}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={value}
            onChange={(e) => setField(field.name, e.target.value)}
          />
        )
      default:
        return (
          <TextField
            key={field.name}
            className={classes.field}
            type={field.type === 'number' ? 'number' : 'text'}
            label={field.label}
            // One short grey line under the input, instead of a paragraph
            // floating above it. See the note on `help` in the field list.
            helperText={field.help}
            variant="outlined"
            size="small"
            fullWidth
            multiline={field.type === 'textarea'}
            minRows={field.type === 'textarea' ? 2 : undefined}
            value={value}
            onChange={(e) =>
              setField(
                field.name,
                field.type === 'number'
                  ? e.target.value === ''
                    ? ''
                    : Number(e.target.value)
                  : e.target.value
              )
            }
          />
        )
    }
  }

  return (
    <ModalForm
      isModalOpen={open}
      onModalClose={onClose}
      modalTitle={title}
      enableSave
      loading={saving}
      buttonLabel="Save"
      onSaveClick={() => {
        // Fields flagged `required` block the save with a plain message rather
        // than letting the API answer "this field may not be blank".
        const missing = visible.filter(
          (f) =>
            f.required &&
            (values[f.name] === undefined ||
              values[f.name] === '' ||
              values[f.name] === null ||
              // An empty MULTISELECT is missing too. `[]` is none of the three
              // above, so a required multi-field slipped through and the save
              // failed at the API instead -- `AtHomeListingSerializer.grades`
              // is a ListField with allow_empty=False, so "[]" came back as a
              // 400 rather than as "Please fill in: Classes...".
              (Array.isArray(values[f.name]) && values[f.name].length === 0))
        )
        if (missing.length) {
          eventAlert(
            `Please fill in: ${missing.map((f) => f.label).join(', ')}`,
            constants.ERROR
          )
          // ModalForm disables Save on click until told otherwise; without
          // this the button stays dead after a validation miss.
          eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
          return
        }
        setSaving(true)
        Promise.resolve(onSubmit(values)).finally(() => setSaving(false))
      }}
      maxWidth={maxWidth}
    >
      {preview ? (
        /* Fields left, preview right, and the two stack on a narrow window
           rather than squeezing the card to nothing. The preview column is
           `sticky` (see CategoryPreview) so it stays in view while a long
           form scrolls past it — the point is to watch the card change as you
           type, which it cannot do once it has scrolled off. */
        <div className={classes.withPreview}>
          <div className={classes.formCol}>{visible.map(renderField)}</div>
          <div className={classes.previewCol}>{preview(values)}</div>
        </div>
      ) : (
        <div>{visible.map(renderField)}</div>
      )}
    </ModalForm>
  )
}

AtHomeFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  maxWidth: PropTypes.string,
  preview: PropTypes.func,
  onClose: PropTypes.func.isRequired
}

export default AtHomeFormModal
